import { html } from '../html.js';
import { DEFAULT_PRINT_OPTIONS } from '../print-options.js';
import { t } from '../strings.js';
import { docDate, field, fields, footer, header, ltr } from './parts.js';
import { notesBlock, party, qtyCell, statusBadge, statusLabel, trackingCell } from './record-parts.js';
/** A header row that exists only when the value was written down; codes print LTR. */
function optionalField(label, value, code) {
    if (value === null || value.trim() === '')
        return [];
    return [field(label, code ? ltr(value) : value)];
}
const QUALITY_KEYS = {
    accepted: 'qualityAccepted',
    rejected: 'qualityRejected',
    partially_accepted: 'qualityPartiallyAccepted',
};
/**
 * PUR-02's goods receipt note as a printed document — the receiving clerk's
 * record of what arrived at the door.
 *
 * A QUANTITY document, the mirror of the delivery note: ORDERED on the
 * purchase order, RECEIVED on this note, ACCEPTED after the quality gate
 * (BR-PUR-02-04 — the accepted figure is the one that reaches the shelf),
 * and REMAINING on the order after it. No money: the supplier's price is the
 * bill's business, and a GRN with values on it would be a second opinion
 * about what the goods cost. The supplier's own reference (their delivery or
 * invoice number) is printed so the two sides can match paper to paper.
 */
export function goodsReceiptHtml(data, locale, company, generatedAt, generatedBy, print = DEFAULT_PRINT_OPTIONS) {
    const tracked = data.lines.some((line) => (line.batchNo !== null && line.batchNo !== '') || line.serialNos.length > 0);
    return html `
    ${header(company, t('goodsReceiptTitle', locale), statusBadge(data.status, locale))}
    ${fields([
        field(t('receiptNo', locale), ltr(data.code)),
        field(t('invoiceDate', locale), docDate(data.receiptDate)),
        field(t('purchaseOrderRef', locale), ltr(data.purchaseOrderCode)),
        field(t('status', locale), statusLabel(data.status, locale)),
        field(t('supplierCode', locale), ltr(data.partnerCode)),
        field(t('warehouse', locale), data.warehouseName),
        ...(data.supplierRef === null || data.supplierRef.trim() === ''
            ? []
            : [field(t('supplierRef', locale), ltr(data.supplierRef))]),
        // Wave 5C — the lorry's paper, each line only when it was written down.
        ...optionalField(t('grnDeliveryNoteNo', locale), data.supplierDeliveryNoteNo, true),
        ...optionalField(t('grnReceivedBy', locale), data.receivedByName, false),
        ...optionalField(t('grnCheckedBy', locale), data.checkedByName, false),
        ...optionalField(t('grnDriver', locale), data.driver, false),
        ...optionalField(t('grnVehicleNo', locale), data.vehicleNo, true),
        ...optionalField(t('grnManualRef', locale), data.manualRef, true),
        ...optionalField(t('grnBookNo', locale), data.bookNo, true),
    ])}
    ${party(t('receivedFrom', locale), data.partnerName)}

    <table class="doc-table">
      <thead>
        <tr>
          <th>${t('lineNo', locale)}</th>
          ${print.showItemCode ? html `<th>${t('itemCode', locale)}</th>` : null}
          <th>${t('description', locale)}</th>
          <th class="amount">${t('qtyOrdered', locale)}</th>
          <th class="amount">${t('qtyReceived', locale)}</th>
          <th class="amount">${t('qtyAccepted', locale)}</th>
          <th class="amount">${t('qtyRemaining', locale)}</th>
          <th>${t('quality', locale)}</th>
          ${tracked ? html `<th>${t('batchNo', locale)} / ${t('serialNos', locale)}</th>` : null}
        </tr>
      </thead>
      <tbody>
        ${data.lines.map((line) => html `
            <tr>
              <td>${ltr(String(line.lineNo))}</td>
              ${print.showItemCode ? html `<td>${line.itemCode === null ? null : ltr(line.itemCode)}</td>` : null}
              <td>
                ${line.description}${line.note === null || line.note.trim() === ''
        ? null
        : html `<br /><small>${line.note}</small>`}
              </td>
              <td class="amount">${qtyCell(line.qtyOrdered, line.unit)}</td>
              <td class="amount">${qtyCell(line.qtyReceived, line.unit)}</td>
              <td class="amount">${qtyCell(line.qtyAccepted, line.unit)}</td>
              <td class="amount">${qtyCell(line.qtyRemaining, line.unit)}</td>
              <td>${t(QUALITY_KEYS[line.quality], locale)}</td>
              ${tracked ? html `<td>${trackingCell(line.batchNo, line.serialNos)}</td>` : null}
            </tr>
          `)}
      </tbody>
    </table>

    ${notesBlock(t('notes', locale), data.notes)}
    ${footer(locale, generatedAt, company.name, generatedBy)}
  `;
}
//# sourceMappingURL=goods-receipt.js.map